import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";

const DEFAULT_FOLLOWUPS = ["What are his skills?", "Tell me about his projects", "How do I contact him?"];

async function fetchReply(question, history = []) {
  const res = await fetch("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ question, history }),
  });
  if (!res.ok) throw new Error("API error");
  return res.json();
}

/* ── Streaming hook ──────────────────────────────────────────────────── */
function useStream() {
  const [streamingId, setStreamingId] = useState(null);
  const [streamedText, setStreamedText] = useState("");
  const timerRef = useRef(null);

  const stream = useCallback((id, fullText, onDone) => {
    clearInterval(timerRef.current);
    setStreamingId(id);
    setStreamedText("");
    let i = 0;
    timerRef.current = setInterval(() => {
      i += 2;
      setStreamedText(fullText.slice(0, i));
      if (i >= fullText.length) {
        clearInterval(timerRef.current);
        setStreamingId(null);
        onDone?.();
      }
    }, 16);
  }, []);

  useEffect(() => () => clearInterval(timerRef.current), []);
  return { streamingId, streamedText, stream };
}

const TypingDots = () => (
  <div style={{ display: "flex", gap: 4, padding: "2px 0" }}>
    {[0, 1, 2].map(i => (
      <span key={i} style={{
        width: 5, height: 5, borderRadius: "50%",
        background: "var(--text-muted)", display: "inline-block",
        animation: "chat-dot 1.2s ease infinite",
        animationDelay: `${i * 0.2}s`,
      }} />
    ))}
  </div>
);


const springSnappy = { type: "spring", stiffness: 420, damping: 30 };
const springGentle = { type: "spring", stiffness: 320, damping: 28 };

/* ── Component ───────────────────────────────────────────────────────── */
const Chat = () => {
  const [input,     setInput]     = useState("");
  const [open,      setOpen]      = useState(false);
  const [messages,  setMessages]  = useState([]);
  const [waiting,   setWaiting]   = useState(false);
  const [followups, setFollowups] = useState([]);
  const bottomRef     = useRef(null);
  const popupInputRef = useRef(null);
  const msgIdRef      = useRef(0);
  const { streamingId, streamedText, stream } = useStream();

  const isStreaming = streamingId !== null;
  const isBusy = waiting || isStreaming;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, waiting, streamedText, followups]);

  useEffect(() => {
    if (open) setTimeout(() => popupInputRef.current?.focus(), 350);
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const processQuestion = useCallback(async (question) => {
    if (!question.trim() || isBusy) return;
    setInput("");
    setOpen(true);
    setFollowups([]);
    setMessages(prev => [...prev, { id: ++msgIdRef.current, from: "user", text: question }]);
    setWaiting(true);

    const replyId = ++msgIdRef.current;

    try {
      const history = messages.filter(m => m.text).slice(-10);
      const { answer, followups: chips } = await fetchReply(question, history);
      setWaiting(false);
      setMessages(prev => [...prev, { id: replyId, from: "ai", text: "" }]);
      stream(replyId, answer, () => {
        setMessages(prev =>
          prev.map(m => m.id === replyId ? { ...m, text: answer } : m)
        );
        setFollowups(chips || DEFAULT_FOLLOWUPS);
      });
    } catch {
      setWaiting(false);
      setMessages(prev => [...prev, {
        id: replyId, from: "ai",
        text: "Couldn't reach the server. Try contacting Rishi directly at riship4611@gmail.com.",
      }]);
      setFollowups(DEFAULT_FOLLOWUPS);
    }
  }, [isBusy, stream, messages]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey && input.trim()) {
      e.preventDefault();
      processQuestion(input);
    }
  };

  const close = () => setOpen(false);

  return (
    <>
      <style>{`
        @keyframes chat-dot {
          0%, 80%, 100% { transform: scale(0.55); opacity: 0.3; }
          40%            { transform: scale(1);    opacity: 1;   }
        }
        @keyframes chat-cursor { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
        .chat-streaming p:last-child::after,
        .chat-streaming li:last-child::after {
          content: "▋";
          display: inline;
          animation: chat-cursor 0.7s step-end infinite;
          color: var(--text-muted);
        }
        @keyframes bar-pulse {
          0%, 100% { box-shadow: inset 0 1px 0 rgba(255,255,255,0.95), 0 4px 20px rgba(0,0,0,0.06); }
          50%       { box-shadow: inset 0 1px 0 rgba(255,255,255,0.95), 0 6px 32px rgba(0,0,0,0.13), 0 0 0 1px rgba(0,0,0,0.04); }
        }
        @keyframes bar-pulse-dark {
          0%, 100% { box-shadow: inset 0 1px 0 rgba(255,255,255,0.05), 0 8px 32px rgba(0,0,0,0.4); }
          50%       { box-shadow: inset 0 1px 0 rgba(255,255,255,0.05), 0 8px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06); }
        }
        .chat-ai {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 4px 16px 16px 16px;
        }
        .chat-ai p:last-child { margin-bottom: 0; }
        .chat-ai ul:last-child,
        .chat-ai ol:last-child { margin-bottom: 0; }
        .chat-user {
          background: var(--text);
          border-radius: 16px 4px 16px 16px;
        }
        .chat-width {
          width: calc(100vw - 32px);
        }
        @media (min-width: 768px) {
          .chat-width { width: 70vw; }
        }
        .chat-chip {
          padding: 6px 13px;
          font-size: 0.78rem;
          font-weight: 500;
          color: var(--text-muted);
          background: transparent;
          border: 1px solid var(--border);
          border-radius: 999px;
          cursor: pointer;
          white-space: nowrap;
          transition: border-color 120ms ease, color 120ms ease, background 120ms ease;
        }
        .chat-chip:hover {
          border-color: var(--border-strong);
          color: var(--text);
          background: var(--bg-card);
        }
        .chat-input-inner {
          flex: 1;
          border: none;
          outline: none;
          background: transparent;
          font-size: 16px;
          color: var(--text);
          min-width: 0;
          font-family: inherit;
        }
        .chat-input-inner::placeholder { color: var(--text-muted); }
        .chat-send-btn {
          width: 34px; height: 34px;
          border-radius: 50%;
          border: none;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          cursor: pointer;
        }
        .chat-bar-glass {
          background: rgba(255,255,255,0.65);
          backdrop-filter: blur(24px) saturate(180%);
          -webkit-backdrop-filter: blur(24px) saturate(180%);
          border: 1px solid rgba(0,0,0,0.09);
          animation: bar-pulse 4s ease-in-out infinite;
        }
        [data-theme="dark"] .chat-bar-glass {
          background: rgba(18,18,18,0.72);
          border: 1px solid rgba(255,255,255,0.09);
          animation: bar-pulse-dark 4s ease-in-out infinite;
        }
      `}</style>

      {/* ── Floating bar ───────────────────────────────────────────── */}
      <AnimatePresence>
        {!open && (
          <div
            className="chat-width"
            style={{
              position: "fixed", bottom: 20, left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1200,
            }}
          >
            <motion.div
              key="bar"
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0,  scale: 1    }}
              exit={{    opacity: 0, y: 10, scale: 0.96 }}
              transition={springGentle}
            >
              <div className="chat-bar-glass" style={{
                display: "flex", alignItems: "center", gap: 12,
                borderRadius: 999,
                padding: "10px 10px 10px 22px",
              }}>
                <input
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  onFocus={() => { if (messages.length > 0) setOpen(true); }}
                  placeholder="Too lazy to scroll? Ask about Rishi →"
                  className="chat-input-inner"
                />
                <motion.button
                  className="chat-send-btn"
                  onClick={() => processQuestion(input)}
                  disabled={!input.trim()}
                  whileTap={{ scale: 0.85 }}
                  transition={{ duration: 0.1 }}
                  style={{ background: input.trim() ? "var(--text)" : "var(--border)", cursor: input.trim() ? "pointer" : "default" }}
                >
                  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                    <path d="M1 13L13 7L1 1V5.5L9 7L1 8.5V13Z" fill={input.trim() ? "var(--bg)" : "var(--text-muted)"} />
                  </svg>
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── Chat popup ─────────────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={close}
              style={{
                position: "fixed", inset: 0, zIndex: 1300,
                background: "rgba(0,0,0,0.48)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }}
            />

            {/* Panel wrapper for centering */}
            <div className="chat-width" style={{
              position: "fixed",
              bottom: 24, left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1400,
            }}>
              <motion.div
                key="panel"
                initial={{ opacity: 0, scale: 0.92, y: 24 }}
                animate={{ opacity: 1, scale: 1,    y: 0  }}
                exit={{    opacity: 0, scale: 0.94, y: 16 }}
                transition={{ type: "spring", stiffness: 380, damping: 32 }}
                style={{
                  background: "var(--bg)",
                  border: "1px solid var(--border)",
                  borderRadius: 20,
                  overflow: "hidden",
                  display: "flex", flexDirection: "column",
                  maxHeight: "80vh",
                  boxShadow: "0 24px 80px rgba(0,0,0,0.2), 0 0 0 1px var(--border)",
                }}
              >
                {/* Close button */}
                <div style={{
                  display: "flex", justifyContent: "flex-end",
                  padding: "12px 14px 0",
                  flexShrink: 0,
                }}>
                  <motion.button
                    onClick={close}
                    whileHover={{ rotate: 90, scale: 1.1 }}
                    whileTap={{ scale: 0.88 }}
                    transition={{ duration: 0.18 }}
                    style={{
                      width: 28, height: 28, borderRadius: "50%",
                      background: "var(--bg-card)",
                      border: "1px solid var(--border-strong)",
                      color: "var(--text-muted)", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "0.7rem",
                    }}
                  >✕</motion.button>
                </div>

                {/* Messages */}
                <div
                  data-lenis-prevent
                  style={{
                    flex: 1, overflowY: "auto",
                    padding: "12px 20px 12px",
                    display: "flex", flexDirection: "column", gap: 12,
                    overscrollBehavior: "contain",
                  }}
                >
                  {messages.map((m) => (
                    <motion.div
                      key={m.id}
                      initial={{ opacity: 0, x: m.from === "user" ? 20 : -20, scale: 0.94 }}
                      animate={{ opacity: 1, x: 0, scale: 1 }}
                      transition={springSnappy}
                      style={{
                        display: "flex",
                        justifyContent: m.from === "user" ? "flex-end" : "flex-start",
                      }}
                    >
                      <div
                        className={m.from === "ai" ? "chat-ai" : "chat-user"}
                        style={{
                          maxWidth: "80%",
                          padding: "10px 14px",
                          fontSize: "0.875rem",
                          lineHeight: 1.6,
                          color: m.from === "user" ? "var(--bg)" : "var(--text)",
                        }}
                      >
                        {m.from === "ai"
                          ? <div className={m.id === streamingId ? "chat-streaming" : undefined}>
                              <ReactMarkdown components={{
                                p:      ({ children }) => <p style={{ margin: "0 0 6px" }}>{children}</p>,
                                ul:     ({ children }) => <ul style={{ margin: "4px 0", paddingLeft: 18 }}>{children}</ul>,
                                ol:     ({ children }) => <ol style={{ margin: "4px 0", paddingLeft: 18 }}>{children}</ol>,
                                li:     ({ children }) => <li style={{ margin: "2px 0" }}>{children}</li>,
                                strong: ({ children }) => <strong style={{ color: "var(--text)", fontWeight: 600 }}>{children}</strong>,
                                code:   ({ children }) => <code style={{ fontFamily: "monospace", fontSize: "0.8em", background: "var(--bg-hover)", padding: "1px 4px", borderRadius: 3 }}>{children}</code>,
                              }}>
                                {m.id === streamingId ? streamedText : m.text}
                              </ReactMarkdown>
                            </div>
                          : m.text
                        }
                      </div>
                    </motion.div>
                  ))}

                  <AnimatePresence>
                    {waiting && (
                      <motion.div
                        key="typing"
                        initial={{ opacity: 0, x: -16, scale: 0.94 }}
                        animate={{ opacity: 1, x: 0,   scale: 1    }}
                        exit={{    opacity: 0, x: -8,  scale: 0.94 }}
                        transition={springSnappy}
                        style={{ display: "flex" }}
                      >
                        <div className="chat-ai" style={{ padding: "10px 14px" }}>
                          <TypingDots />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Follow-up chips — staggered */}
                  <AnimatePresence>
                    {followups.length > 0 && !isBusy && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{ display: "flex", flexWrap: "wrap", gap: 8, paddingTop: 4 }}
                      >
                        {followups.map((q, i) => (
                          <motion.button
                            key={q}
                            className="chat-chip"
                            initial={{ opacity: 0, scale: 0.85, y: 8 }}
                            animate={{ opacity: 1, scale: 1,    y: 0 }}
                            transition={{ ...springSnappy, delay: i * 0.07 }}
                            onClick={() => processQuestion(q)}
                          >{q}</motion.button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div ref={bottomRef} />
                </div>

                {/* Input — docked at bottom */}
                <div style={{ padding: "12px 16px", flexShrink: 0 }}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 10,
                    background: "var(--bg-card)",
                    border: "1px solid var(--border-strong)",
                    borderRadius: 999,
                    padding: "8px 8px 8px 16px",
                  }}>
                    <input
                      ref={popupInputRef}
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      onKeyDown={handleKey}
                      placeholder="Ask a follow-up…"
                      className="chat-input-inner"
                      style={{ fontSize: "16px" }}
                    />
                    <motion.button
                      className="chat-send-btn"
                      onClick={() => processQuestion(input)}
                      disabled={!input.trim() || isBusy}
                      whileTap={{ scale: 0.85 }}
                      transition={{ duration: 0.1 }}
                      style={{ background: input.trim() && !isBusy ? "var(--text)" : "var(--border)", cursor: input.trim() && !isBusy ? "pointer" : "default" }}
                    >
                      <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                        <path d="M1 13L13 7L1 1V5.5L9 7L1 8.5V13Z" fill={input.trim() && !isBusy ? "var(--bg)" : "var(--text-muted)"} />
                      </svg>
                    </motion.button>
                  </div>
                  <p style={{ fontSize: "0.65rem", color: "var(--text-muted)", textAlign: "center", margin: "8px 0 0" }}>
                    Powered by Groq · responses may vary
                  </p>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Chat;
